<script lang="ts">
import { defineComponent, computed, reactive } from '@vue/composition-api';
import UserNavGroup from './UserNavGroup.vue';
import router from '@/_router';
import { currentUserWsRoles, hasStorymapAccess } from '@/_core/utils/roles';
import { useIdeaPortalSlugStore } from '../main/feedbackportal/components/ideaPortal/use';
import * as productsStoreModule from '@/store/main/Products';
import { subscriptionManager, FeatureAddOn } from '@/_core/utils/features';
import AddNewProductDialog from '@/pages/main/manageProducts/components/AddNewProductDialog.vue';
import UserFeedback from './UserFeedback.vue';

// @ts-ignore
import ClickOutside from 'vuetify/lib/directives/click-outside';
import { useIsOpen } from '@/_core/utils/use/useIsOpen';
import { confirm } from '@/_core/app-components/use';

interface IProps {
    isOpen: boolean;
    storedDrawer: boolean;
}

const NavDrawer = defineComponent({
    name: 'NavDrawer',

    directives: {
        ClickOutside,
    },
    components: {
        UserNavGroup,
        AddNewProductDialog,
        UserFeedback,
    },
    props: { isOpen: Boolean, storedDrawer: Boolean },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props: IProps, ctx) {
        const store = productsStoreModule.useProductsStore();

        const drawer = computed({
            get: () => props.isOpen,
            set: (val) => {
                ctx.emit('openChanged', val);
            },
        });

        const onCloseNavDrawer = () => {
            ctx.emit('openChanged', false, true);
        };

        const onOpenNavDrawer = () => {
            ctx.emit('openChanged', true, true);
        };

        const ideaPortalSlugStore = useIdeaPortalSlugStore();

        const state = reactive({
            isInWsContext: initAppData.isInWsContext,
            isCorporateSubscriptionManager: initAppData.isCorporateSubscriptionManager,
            isAdmin: initAppData.isInWsContext && (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin),
            isObserver: currentUserWsRoles.isObserver,
            hasRoadmapsFeatureAddon: computed(() => subscriptionManager.hasFeatureAddOn(FeatureAddOn.BasicRoadmaps)),
            products: computed(() => store.state.products),
            currentProductSlug: computed(() => store.state.currentProductSlug),
            currenttProduct: computed(() => store.state.products.find((x) => x.slug === store.state.currentProductSlug)),
            addNewProductDialog: useIsOpen(),
            canCreateMoreProducts: computed(() => {
                return store.state.products.length < initAppData.currentWs.productLimit;
            }),
        });

        const onOpenUserProfile = () => {
            ctx.emit('openUserProfile');
        };

        const onOpenApiTokenDialog = () => {
            ctx.emit('openApiTokenDialog');
        };

        const onOpenNotificationSettings = () => {
            ctx.emit('openNotificationSettings');
        };

        const onOpenFeedbackPortal = () => {
            ctx.emit('openFeedbackPortal');
        };

        const goToDashboard = (newTab: boolean) => {
            if (newTab) {
                const url = router.resolve('/');
                window.open(url.href, '_blank');
            } else if (ctx.root.$route.path !== '/') {
                router.push('/');
            }
        };

        const onNavDrawerClick = () => {
            if (props.isOpen && ctx.root.$vuetify.breakpoint.mdAndUp && !props.storedDrawer) {
                ctx.emit('openChanged', false);
            }
        };

        const onOutsideNavDrawerClick = () => {
            onNavDrawerClick();
        };

        const changeProduct = (slug: string) => {
            store.dispatch(new productsStoreModule.ChangeCurrentProduct(slug));
            onOpenCurrentProduct();
        };

        const onOpenCurrentProduct = () => {
            if (state.currentProductSlug == null) {
                ctx.root.$router.push('/products');
            } else if (!ctx.root.$route.fullPath.startsWith(`/${state.currentProductSlug}/dashboard`)) {
                ctx.root.$router.push(`/${state.currentProductSlug}/dashboard`);
            }
        };

        const onProductAdded = async (id: string, name: string, slug: string) => {
            await store.dispatch(new productsStoreModule.ProductCreated(id, slug, name));
            changeProduct(slug);
        };

        const onAddNewProduct = () => {
            if (state.canCreateMoreProducts) {
                state.addNewProductDialog.open();
            } else {
                confirm.show({
                    title: "You've reached the maximum number of products allowed with your current subscription plan",
                    message: "To add a new product, you'll need to either upgrade your subscription or remove an existing product.",
                    width: 700,
                    acceptBtnText: 'Upgrade subscription',
                    acceptFn: () => ctx.root.$router.push('/managesubscription'),
                });
            }
        };

        return {
            ctx,
            state,
            hasStorymapAccess,
            drawer,
            onCloseNavDrawer,
            onOpenNavDrawer,
            onOpenUserProfile,
            onOpenNotificationSettings,
            onOpenApiTokenDialog,
            onOpenFeedbackPortal,
            goToDashboard,
            helpCenterUrl: helpLinks.helpLinkHelpCenter,
            portalSlug: ideaPortalSlugStore.portalSlug,

            onNavDrawerClick,
            onOutsideNavDrawerClick,
            changeProduct,
            onOpenCurrentProduct,
            onProductAdded,
            onAddNewProduct,
        };
    },
});

export default NavDrawer;
</script>

<template>
  <!-- color="white" -->
  <!-- v-click-outside="{ handler: onOutsideNavDrawerClick }" -->
  <v-navigation-drawer
    v-model="drawer"
    :mini-variant="false"
    app
    class="navigation-drawer"
    color="white"
    width="240px"
    :temporary="ctx.root.$vuetify.breakpoint.mdAndUp && !storedDrawer"
  >
    <AddNewProductDialog
      v-if="state.addNewProductDialog.isOpen"
      :isOpen="state.addNewProductDialog.isOpen"
      @close="state.addNewProductDialog.close"
      @productAdded="onProductAdded"
    />

    <v-list class="pb-2">
      <v-list-item
        class="logo mt-2 d-flex align-center"
        style="min-height: auto;"
        @click.ctrl.exact="goToDashboard(true)"
        @click.middle.exact="goToDashboard(true)"
        @click.exact="goToDashboard(false)"
      >
        <img
          :src="require('@/assets/images/StoriesOnBoard_Logo.svg')"
          width="140px"
          class="mr-auto"
        >
        <template v-if="ctx.root.$vuetify.breakpoint.mdAndUp">
          <v-icon v-if="storedDrawer" right @click.stop="onCloseNavDrawer">
            $falChevronsLeft
          </v-icon>
        
          <v-icon v-else right @click.stop="onOpenNavDrawer">
            $falChevronsRight
          </v-icon>
        </template>
      </v-list-item>
    </v-list>

    <v-list nav class="navdrawer-menu pt-6">
      <v-list-item v-if="state.isCorporateSubscriptionManager" class="font-weight-medium" to="/corporatesubscriptionmanagement">
        <v-list-item-icon class="mr-3 my-0 py-2">
          <v-icon>
            $falBuilding
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content class="py-0">
          <v-list-item-title class="blueGrey90Light--text">
            Corporate settings
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <!-- class="blueGrey90Light--text surface navigation-item" active-class="active-navigation-item" -->
      <v-list-item class="font-weight-medium" to="/">
        <v-list-item-icon class="mr-3 my-0 py-2">
          <v-icon>
            $home
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content class="py-0">
          <v-list-item-title class="blueGrey90Light--text">
            Dashboard
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item
        v-if="state.isInWsContext"
        class="font-weight-medium mt-3"
        style="border: 1px solid var(--v-blueGrey30Light-base); border-radius: 4px;"
        @click="onOpenCurrentProduct"
      >
        <v-list-item-icon class="mr-3 my-0 py-0 align-self-center">
          <v-icon>
            $fatRocketLaunch
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content class="py-0">
          <v-list-item-title v-if="state.currentProductSlug == null || state.currenttProduct == null" class="blueGrey90Light--text">
            No products
          </v-list-item-title>
          <v-list-item-title v-else class="blueGrey90Light--text">
            {{ state.currenttProduct.name }}
          </v-list-item-title>
        </v-list-item-content>

        <v-menu offset-x>
          <template #activator="{ on: on, attrs: attrs }">
            <v-list-item-icon
              v-bind="attrs"
              v-on="on"
              @click.stop
            >
              <v-btn small icon>
                <v-icon small>
                  $farChevronDown
                </v-icon>
              </v-btn>
            </v-list-item-icon>
          </template>
          <v-card>
            <v-list nav dense>
              <div style="max-height: 200px; overflow-y: auto;" class="thin-scrollbar">
                <v-list-item
                  v-for="product in state.products"
                  :key="product.slug"
                  @click="changeProduct(product.slug)"
                >
                  <v-list-item-icon>
                    <v-icon>$fatRocketLaunch</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ product.name }}</v-list-item-title>
                </v-list-item>
              </div>

              <v-list-item to="/products">
                <v-list-item-icon> 
                  <v-icon>
                    $grid-light
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  All products
                </v-list-item-title>
              </v-list-item>

              <v-list-item v-if="state.isAdmin" @click="onAddNewProduct">
                <v-list-item-icon>
                  <v-icon>
                    $add-item
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  Create new product
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-list-item>

      <template v-if="state.currentProductSlug != null">
        <v-list-item class="insight-menu-item font-weight-medium" :to="`/${state.currentProductSlug}/feedbackportal/insights`">
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>
              $insight-menu-icon
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text">
              Insights
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item class="font-weight-medium" :to="`/${state.currentProductSlug}/feedbackportal/ideas`">
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>
              $bulb
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text">
              Ideas
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      
        <v-list-item class="font-weight-medium" :to="`/${state.currentProductSlug}/feedbackportal/p/${portalSlug}?loadDefaultPortalIfNotFound=1`">
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>
              $globe-light
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text">
              Portal
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="!state.isObserver"
        
          style="min-height: auto;"
          class=" font-weight-medium"
          :to="`/${state.currentProductSlug}/managepersonas`"
        >
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>$falUser</v-icon>
          </v-list-item-icon>
        
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text">
              Personas
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="hasStorymapAccess"
          style="min-height: auto;"
          class=" font-weight-medium"
          :to="`/${state.currentProductSlug}/storymaps`"
        >
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>
              $storymaps
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text">
              Story maps
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
          
        <v-list-item
          style="min-height: auto;"
          class=" font-weight-medium"
          :to="`/${state.currentProductSlug}/roadmaps`"
        >
          <v-list-item-icon class="mr-3 my-0 py-2">
            <v-icon>$falChartGantt</v-icon>
          </v-list-item-icon>
        
          <v-list-item-content class="py-0">
            <v-list-item-title class="blueGrey90Light--text py-1">
              Roadmaps
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>

    <v-spacer />

    <v-list nav class="">
      <portal-target name="navBottom" />      
      <v-list-item v-if="state.isInWsContext" class="mb-0" to="/ai">
        <v-list-item-avatar
          style="border-radius: 50% !important; cursor: default;  width: 40px !important; min-width: 40px !important; height: 40px !important; font-size: 16px; font-weight: 700;"
          class=" font-weight-bold"
        >
          <v-icon color="teal70Light" size="20">
            $fasSparkles
          </v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title class="teal70Light--text">
            AI Assist
            <v-chip
              x-small
              label
              class="ml-2"
              text-color="white"
              color="teal70Light"
            >
              NEW
            </v-chip>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="state.isInWsContext && !state.isObserver" class="mb-0" to="/managepeople">
        <v-list-item-avatar
          style="border-radius: 50% !important; cursor: default;  width: 40px !important; min-width: 40px !important; height: 40px !important; font-size: 16px; font-weight: 700;"
          class=" font-weight-bold"
        >
          <v-icon color="blueGrey90Light" size="24">
            $falUsers
          </v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>Users</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <UserFeedback />

      <UserNavGroup
        @openUserProfile="onOpenUserProfile"
        @openNotificationSettings="onOpenNotificationSettings"
        @openApiTokenDialog="onOpenApiTokenDialog"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.logo {
    &:hover {
        background-color: transparent !important;
    }
    &:before {
        opacity: 0 !important;
    }
}

::v-deep .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        width: 6px;
    }
}

.narrow-append-slot ::v-deep .v-list-group__header__append-icon {
    min-width: 10px !important;
    margin-left: 0 !important;
}

.navdrawer-menu ::v-deep .v-list-item {
    min-height: 40px;
    margin-bottom: 2px !important;
}

::v-deep .insight-menu-item {
    &.v-list-item--active,
    &:hover,
    &:focus {
        svg path {
            stroke: var(--v-blue50Light-base) !important;
        }
    }
}
</style>
